<template>
    <v-dialog v-model="open" max-width="560" scrollable>
        <v-card>
            <v-card-title class="d-flex align-center py-3 px-4">
                <v-icon icon="mdi-cog" class="mr-2" />
                設定
            </v-card-title>

            <v-tabs v-model="tab" density="compact" show-arrows>
                <v-tab value="general">一般</v-tab>
                <v-tab value="skillAlias">
                    技能別稱
                    <v-chip v-if="aliasCount > 0" size="x-small" color="primary" variant="tonal" class="ml-1">
                        {{ aliasCount }}
                    </v-chip>
                </v-tab>
                <v-tab value="condAlias">
                    CC 別稱
                    <v-chip v-if="condAliasCount > 0" size="x-small" color="primary" variant="tonal" class="ml-1">
                        {{ condAliasCount }}
                    </v-chip>
                </v-tab>
                <v-tab value="luck">人品</v-tab>
            </v-tabs>
            <v-divider />

            <v-card-text style="min-height: 260px; max-height: 60vh;">
                <v-window v-model="tab">

                    <!-- ── 一般設定 ── -->
                    <v-window-item value="general">
                        <div class="text-subtitle-2 mb-2 mt-1">顯示語言</div>
                        <v-select
                            v-model="currentLang"
                            :items="LANG_OPTIONS"
                            item-title="label"
                            item-value="value"
                            density="compact"
                            hide-details
                            variant="outlined"
                            class="mb-1"
                        />
                        <div class="text-caption text-medium-emphasis mb-3">
                            切換後會自動從伺服器下載對應語言的名稱資料（首次需連線）
                        </div>

                        <v-divider class="mb-2" />

                        <div class="text-subtitle-2 mb-1">顯示選項</div>
                        <v-checkbox
                            :model-value="showDate"
                            @update:model-value="setShowDate($event as boolean)"
                            label="在 Summary / Summary V2 / 玩家比較 顯示日期"
                            density="compact"
                            hide-details
                            class="mb-2"
                        />
                        <v-checkbox
                            v-model="simplifyNumbers"
                            label="顯示數字簡化"
                            density="compact"
                            hide-details
                            color="primary"
                        />
                        <v-checkbox
                            v-model="ignoreSummonsExceptPuppet"
                            label="忽略人偶以外的召喚物"
                            density="compact"
                            hide-details
                            color="primary"
                        />

                        <v-divider class="mb-3" />

                        <div class="text-subtitle-2 mb-2">隱藏的狀態（CC）</div>
                        <div v-if="hiddenCCList.length === 0" class="text-body-2 text-medium-emphasis">
                            無隱藏狀態。在 CC 圖示上 Shift+Click 可隱藏。
                        </div>
                        <v-list v-else density="compact">
                            <v-list-item v-for="ccId in hiddenCCList" :key="ccId">
                                <template #prepend>
                                    <img width="16" height="16"
                                        :src="condIconUrl(region, ccId)"
                                        class="mr-2" />
                                </template>
                                <v-list-item-title>{{ condNameMap[ccId] ?? `CC ${ccId}` }}</v-list-item-title>
                                <template #append>
                                    <v-btn icon="mdi-delete" size="x-small" variant="text" color="error"
                                        @click="onRemoveHiddenCC(ccId)" />
                                </template>
                            </v-list-item>
                        </v-list>

                        <v-divider class="my-3" />

                        <div class="text-subtitle-2 mb-2">隱藏的怪物種族</div>
                        <div v-if="hiddenRaceList.length === 0" class="text-body-2 text-medium-emphasis">
                            無隱藏種族。在 Take Damage 頁的 × 按鈕可隱藏。
                        </div>
                        <v-list v-else density="compact">
                            <v-list-item v-for="raceId in hiddenRaceList" :key="raceId">
                                <v-list-item-title>{{ raceNameMap[raceId] ?? `Race ${raceId}` }}</v-list-item-title>
                                <template #append>
                                    <v-btn icon="mdi-delete" size="x-small" variant="text" color="error"
                                        @click="onRemoveHiddenRace(raceId)" />
                                </template>
                            </v-list-item>
                        </v-list>
                    </v-window-item>

                    <!-- ── 技能別稱 ── -->
                    <v-window-item value="skillAlias">
                        <!-- 新增列 -->
                        <div class="d-flex align-center mt-1 mb-3" style="gap: 8px;">
                            <v-autocomplete
                                v-model="newAliasSkillId"
                                :items="skillSelectItems"
                                item-title="title"
                                item-value="value"
                                label="選擇技能"
                                density="compact"
                                hide-details
                                clearable
                                auto-select-first
                                style="flex: 1"
                            />
                            <v-text-field
                                v-model="newAliasText"
                                label="別稱"
                                density="compact"
                                hide-details
                                style="flex: 1"
                                @keydown.enter="addAlias"
                            />
                            <v-btn
                                icon="mdi-plus"
                                size="small"
                                variant="tonal"
                                color="primary"
                                :disabled="!newAliasSkillId || !newAliasText.trim()"
                                @click="addAlias"
                            />
                        </div>

                        <!-- 現有別稱列表 -->
                        <div v-if="aliasList.length === 0" class="text-body-2 text-medium-emphasis text-center py-4">
                            尚無技能別稱。<br>
                            也可以在 Summary 的技能明細直接點擊技能名稱來設定。
                        </div>
                        <v-list v-else density="compact">
                            <v-list-item
                                v-for="item in aliasList"
                                :key="item.skillId"
                                :subtitle="`原名：${item.originalName}`"
                                style="cursor: pointer;"
                                @click="loadAlias(item)"
                            >
                                <template #prepend>
                                    <span class="text-caption text-disabled mr-2" style="min-width: 48px; text-align: right;">
                                        #{{ item.skillId }}
                                    </span>
                                </template>
                                <v-list-item-title class="font-weight-medium" style="color: #00d4c8;">
                                    {{ item.alias }}
                                </v-list-item-title>
                                <template #append>
                                    <v-btn icon="mdi-delete" size="x-small" variant="text" color="error"
                                        @click.stop="onRemoveAlias(item.skillId)" />
                                </template>
                            </v-list-item>
                        </v-list>

                        <!-- 全部清除 -->
                        <div v-if="aliasList.length > 0" class="d-flex justify-end mt-3">
                            <v-btn size="x-small" variant="text" color="error" prepend-icon="mdi-delete-sweep"
                                @click="clearAllAliases">
                                全部清除
                            </v-btn>
                        </div>
                    </v-window-item>

                    <!-- ── CC 別稱 ── -->
                    <v-window-item value="condAlias">
                        <!-- 新增列 -->
                        <div class="d-flex align-center mt-1 mb-3" style="gap: 8px;">
                            <v-autocomplete
                                v-model="newCondAliasCCId"
                                :items="condSelectItems"
                                item-title="title"
                                item-value="value"
                                label="選擇 CC"
                                density="compact"
                                hide-details
                                clearable
                                auto-select-first
                                style="flex: 1"
                            />
                            <v-text-field
                                v-model="newCondAliasText"
                                label="別稱"
                                density="compact"
                                hide-details
                                style="flex: 1"
                                @keydown.enter="addCondAlias"
                            />
                            <v-btn
                                icon="mdi-plus"
                                size="small"
                                variant="tonal"
                                color="primary"
                                :disabled="!newCondAliasCCId || !newCondAliasText.trim()"
                                @click="addCondAlias"
                            />
                        </div>

                        <!-- 現有別稱列表 -->
                        <div v-if="condAliasList.length === 0" class="text-body-2 text-medium-emphasis text-center py-4">
                            尚無 CC 別稱。
                        </div>
                        <v-list v-else density="compact">
                            <v-list-item
                                v-for="item in condAliasList"
                                :key="item.ccId"
                                :subtitle="`原名：${item.originalName}`"
                                style="cursor: pointer;"
                                @click="loadCondAlias(item)"
                            >
                                <template #prepend>
                                    <img width="16" height="16"
                                        :src="condIconUrl(region, item.ccId)"
                                        class="mr-2" style="flex-shrink:0" />
                                </template>
                                <v-list-item-title class="font-weight-medium" style="color: #00d4c8;">
                                    {{ item.alias }}
                                </v-list-item-title>
                                <template #append>
                                    <v-btn icon="mdi-delete" size="x-small" variant="text" color="error"
                                        @click.stop="onRemoveCondAlias(item.ccId)" />
                                </template>
                            </v-list-item>
                        </v-list>

                        <!-- 全部清除 -->
                        <div v-if="condAliasList.length > 0" class="d-flex justify-end mt-3">
                            <v-btn size="x-small" variant="text" color="error" prepend-icon="mdi-delete-sweep"
                                @click="clearAllCondAliases">
                                全部清除
                            </v-btn>
                        </div>
                    </v-window-item>

                    <!-- ── 人品設定 ── -->
                    <v-window-item value="luck">
                        <div class="text-subtitle-2 mt-1 mb-2">暴擊率</div>
                        <div class="d-flex align-center mb-3" style="gap: 8px">
                            <v-text-field v-model.number="luckConfig.critRateBase" label="基礎暴擊率 %" type="number" density="compact" hide-details style="max-width: 130px" />
                            <span class="text-body-2">結界加成</span>
                            <v-text-field v-model.number="luckConfig.critRateBarrierBonus" label="+% " type="number" density="compact" hide-details style="max-width: 90px" />
                        </div>
                        <v-divider class="mb-2" />
                        <div class="text-subtitle-2 mb-2">連續攻擊觸發率</div>
                        <div class="d-flex align-center mb-3" style="gap: 8px">
                            <v-text-field v-model.number="luckConfig.chainRateBase" label="基礎觸發率 %" type="number" density="compact" hide-details style="max-width: 130px" />
                            <span class="text-body-2">破壞套加成</span>
                            <v-text-field v-model.number="luckConfig.chainRateSetBonus" label="+% " type="number" density="compact" hide-details style="max-width: 90px" />
                        </div>
                        <v-divider class="mb-2" />
                        <div class="text-subtitle-2 mb-2">星塵</div>
                        <div v-for="cfg in luckConfig.stardustConfigs" :key="cfg.id" class="d-flex flex-wrap align-center mb-2" style="gap: 8px">
                            <v-text-field v-model="cfg.label" label="名稱" density="compact" hide-details style="max-width: 90px" />
                            <v-autocomplete
                                v-model="cfg.skillId"
                                :items="skillSelectItems"
                                item-title="title"
                                item-value="value"
                                label="技能"
                                density="compact"
                                hide-details
                                clearable
                                auto-select-first
                                style="min-width: 160px; max-width: 220px"
                                @update:model-value="(v) => cfg.skillId = v ?? 0"
                            />
                            <v-text-field v-model.number="cfg.rate" label="觸發率 %" type="number" density="compact" hide-details style="max-width: 90px" />
                            <v-text-field v-model.number="cfg.cooldownSec" label="CD 秒" type="number" density="compact" hide-details style="max-width: 80px" />
                        </div>
                    </v-window-item>

                </v-window>
            </v-card-text>

            <v-divider />
            <v-card-actions class="px-4 py-2">
                <v-spacer />
                <v-btn color="primary" variant="flat" @click="open = false">關閉</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import { defineComponent, inject, computed, ref, type Ref } from 'vue';
import { condIconUrl } from "@/effectState";
import { removeHiddenCC, removeHiddenRace, skillAliases, setSkillAlias, removeSkillAlias, condAliases, setCondAlias, removeCondAlias, setLang, setShowDate } from '@/store';
import { ignoreSummonsExceptPuppet, luckConfig } from '@/summaryConfig';
import { simplifyNumbers } from '@/lib/util';

export default defineComponent({
    props: {
        modelValue: { type: Boolean, required: true },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const region      = inject('region') as Ref<string>;
        const lang        = inject('lang')   as Ref<string>;
        const condNameMap = inject('condNameMap') as Ref<Record<number, string>>;
        const raceNameMap = inject('raceNameMap') as Ref<Record<number, string>>;
        const skillNameMap = inject('skillNameMap') as Ref<Record<number, string>>;
        const hiddenCCIds   = inject('hiddenCCIds')   as Ref<Set<number>>;
        const hiddenRaceIds = inject('hiddenRaceIds') as Ref<Set<number>>;
        const showDate      = inject('showDate')      as Ref<boolean>;

        const LANG_OPTIONS = [
            { value: 'tw', label: '繁體中文 (TW)' },
            { value: 'cn', label: '简体中文 (CN)' },
            { value: 'kr', label: '한국어 (KR)' },
            { value: 'jp', label: '日本語 (JP)' },
            { value: 'us', label: 'English (US)' },
        ];

        const currentLang = computed({
            get: () => lang.value,
            set: (v: string) => setLang(v),
        });

        const open = computed({
            get: () => props.modelValue,
            set: (v) => emit('update:modelValue', v),
        });

        const tab = ref('general');

        // ── 一般設定 ────────────────────────────────────────────
        const hiddenCCList   = computed(() => [...hiddenCCIds.value]);
        const hiddenRaceList = computed(() => [...hiddenRaceIds.value]);
        const onRemoveHiddenCC   = (ccId: number)   => removeHiddenCC(ccId);
        const onRemoveHiddenRace = (raceId: number) => removeHiddenRace(raceId);

        // ── 技能別稱 ─────────────────────────────────────────────
        const newAliasSkillId = ref<number | null>(null);
        const newAliasText    = ref('');

        const skillSelectItems = computed(() =>
            Object.entries(skillNameMap.value)
                .map(([id, name]) => ({ title: `${name}（${id}）`, value: Number(id) }))
                .sort((a, b) => a.value - b.value),
        );

        const aliasList = computed(() =>
            Object.entries(skillAliases.value)
                .map(([id, alias]) => {
                    const skillId = Number(id);
                    return {
                        skillId,
                        alias,
                        originalName: skillNameMap.value[skillId] ?? `#${skillId}`,
                    };
                })
                .sort((a, b) => a.skillId - b.skillId),
        );

        const aliasCount = computed(() => Object.keys(skillAliases.value).length);

        function loadAlias(item: { skillId: number; alias: string }) {
            newAliasSkillId.value = item.skillId;
            newAliasText.value = item.alias;
        }

        function addAlias() {
            if (!newAliasSkillId.value || !newAliasText.value.trim()) return;
            setSkillAlias(newAliasSkillId.value, newAliasText.value.trim());
            newAliasSkillId.value = null;
            newAliasText.value = '';
        }

        function onRemoveAlias(skillId: number) {
            removeSkillAlias(skillId);
        }

        function clearAllAliases() {
            for (const id of Object.keys(skillAliases.value)) {
                removeSkillAlias(Number(id));
            }
        }

        // ── CC 別稱 ──────────────────────────────────────────────
        const newCondAliasCCId = ref<number | null>(null);
        const newCondAliasText = ref('');

        const condSelectItems = computed(() =>
            Object.entries(condNameMap.value)
                .map(([id, name]) => ({ title: `${name}（${id}）`, value: Number(id) }))
                .sort((a, b) => a.value - b.value),
        );

        const condAliasList = computed(() =>
            Object.entries(condAliases.value)
                .map(([id, alias]) => {
                    const ccId = Number(id);
                    return {
                        ccId,
                        alias,
                        originalName: condNameMap.value[ccId] ?? `CC ${ccId}`,
                    };
                })
                .sort((a, b) => a.ccId - b.ccId),
        );

        const condAliasCount = computed(() => Object.keys(condAliases.value).length);

        function loadCondAlias(item: { ccId: number; alias: string }) {
            newCondAliasCCId.value = item.ccId;
            newCondAliasText.value = item.alias;
        }

        function addCondAlias() {
            if (!newCondAliasCCId.value || !newCondAliasText.value.trim()) return;
            setCondAlias(newCondAliasCCId.value, newCondAliasText.value.trim());
            newCondAliasCCId.value = null;
            newCondAliasText.value = '';
        }

        function onRemoveCondAlias(ccId: number) {
            removeCondAlias(ccId);
        }

        function clearAllCondAliases() {
            for (const id of Object.keys(condAliases.value)) {
                removeCondAlias(Number(id));
            }
        }

        return {
            open, tab, region,
            condIconUrl,
            condNameMap, raceNameMap,
            hiddenCCList, hiddenRaceList,
            onRemoveHiddenCC, onRemoveHiddenRace,
            LANG_OPTIONS, currentLang,
            showDate, setShowDate,
            ignoreSummonsExceptPuppet,
            simplifyNumbers,
            skillSelectItems, aliasList, aliasCount,
            newAliasSkillId, newAliasText,
            loadAlias, addAlias, onRemoveAlias, clearAllAliases,
            condSelectItems, condAliasList, condAliasCount,
            newCondAliasCCId, newCondAliasText,
            loadCondAlias, addCondAlias, onRemoveCondAlias, clearAllCondAliases,
            luckConfig,
        };
    },
});
</script>
